"use client";

import Chevron from "@/app/assets/images/svg/chevron.svg";
import clsx from "clsx";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { FC, useMemo } from "react";
import ReactPaginate from "react-paginate";
import styles from "./Pagination.module.scss";

interface Props {
	initialPage?: number;
	page?: number;
	pageCount?: number;
	onPageChange?: (page: number) => void;
	className?: string;
	routing?: "window" | "next" | "none";
	prefetchCount?: number; // Number of pages to prefetch on either side of the current page
	forcePage?: number; // Force the current page to a specific value, overriding internal state
}

const Pagination: FC<Props> = ({
	initialPage,
	pageCount = 0,
	onPageChange,
	className = "",
	routing = "window",
	forcePage,
}) => {
	pageCount = Math.ceil(pageCount);

	const pathname = usePathname();
	const params = useSearchParams();
	const router = useRouter();

	const getPageUrl = (pageNum: number): string => {
		const updatedParams = new URLSearchParams(params.toString());

		// don't include page param on first page
		if (pageNum === 0) {
			updatedParams.delete("page");
		} else {
			updatedParams.set("page", `${pageNum + 1}`);
		}

		const paramsString = updatedParams.toString();
		return `${pathname}${paramsString ? `?${paramsString}` : ""}`;
	};

	const updatePageParam = (page: number) => {
		if (routing === "none") {
			return;
		}

		const href = getPageUrl(page);

		if (routing === "window") {
			return window.history.replaceState(null, "", href);
		}

		if (routing === "next") {
			return router.push(href);
		}
	};

	const initialPageMemo = useMemo(() => {
		if (!pageCount || forcePage !== undefined) {
			return undefined;
		}

		const pageParam = params.get("page");
		const page = initialPage ? initialPage : Number(pageParam || 1);

		// check for page is greater than total pages
		let validPage = page > pageCount ? pageCount : page;

		//check to if page is less than 1 (the lowest)
		validPage = validPage < 1 ? 1 : validPage;

		validPage = validPage - 1;

		// update page param is it doesn't match
		if (validPage !== page - 1) {
			updatePageParam(validPage);
		}

		return validPage;
	}, [pageCount]);

	const handlePageChange = (page: number) => {
		updatePageParam(page);
		onPageChange?.(page);
	};

	// Prefetch adjacent pages when current page changes
	// useEffect(() => {
	// 	if (currentPage === undefined || !pageCount) return;

	// 	// Prefetch next pages sequentially
	// 	for (let i = 1; i <= prefetchCount; i++) {
	// 		const pageToFetch = currentPage + i;
	// 		if (pageToFetch < pageCount) {
	// 			prefetchPage(pageToFetch);
	// 		}
	// 	}

	// 	// Prefetch previous pages sequentially
	// 	for (let i = 1; i <= prefetchCount; i++) {
	// 		const pageToFetch = currentPage - i;
	// 		if (pageToFetch >= 0) {
	// 			prefetchPage(pageToFetch);
	// 		}
	// 	}
	// }, [currentPage, pageCount, prefetchCount]);

	if (!pageCount) {
		return null;
	}

	return (
		<nav
			className={clsx([className, styles.outer])}
			aria-label='pagination'>
			<ReactPaginate
				initialPage={initialPageMemo}
				forcePage={forcePage !== undefined ? forcePage - 1 : undefined}
				previousLabel={<Chevron aria-labelledby='directional chevron' />}
				nextLabel={<Chevron aria-labelledby='directional chevron' />}
				pageCount={pageCount}
				onPageChange={({ selected }) => handlePageChange(selected)}
				containerClassName={styles.container}
				previousClassName={styles.previous}
				pageLinkClassName={styles.item}
				nextClassName={styles.next}
				disabledClassName={styles.disabled}
				activeClassName={styles.active}
				breakLabel={"..."}
				breakClassName={styles.break}
				pageRangeDisplayed={5}
				marginPagesDisplayed={1}
			/>
		</nav>
	);
};

export default Pagination;
